import { useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, tokenRequest } from '@/config/msal';
import { useAuthStore } from '@/stores/authStore';

export function useAuth() {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user, clearUser, updateLastActivity } = useAuthStore();

  const signIn = useCallback(async () => {
    try {
      // Ensure any pending redirect is processed first
      await instance.handleRedirectPromise();
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      // Ignore interaction_in_progress - redirect is already happening
      if (error instanceof Error && error.message?.includes('interaction_in_progress')) {
        return;
      }
      console.error('Login failed:', error);
    }
  }, [instance]);

  const signOut = useCallback(async () => {
    try {
      clearUser();
      await instance.logoutRedirect({
        postLogoutRedirectUri: '/auth/signin',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [instance, clearUser]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    // Get active account or first available account
    let account = instance.getActiveAccount();

    if (!account && accounts.length > 0) {
      account = accounts[0];
      instance.setActiveAccount(account);
    }

    if (!account) {
      console.warn('No MSAL account available - user needs to sign in');
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...tokenRequest,
        account,
      });
      updateLastActivity();
      return response.accessToken;
    } catch (error) {
      console.warn('Silent token acquisition failed, trying popup:', error);
      // If silent acquisition fails, try popup
      try {
        const response = await instance.acquireTokenPopup(tokenRequest);
        updateLastActivity();
        return response.accessToken;
      } catch (popupError) {
        console.error('Token acquisition failed:', popupError);
        return null;
      }
    }
  }, [instance, accounts, updateLastActivity]);

  return {
    user,
    isAuthenticated,
    isLoading: inProgress !== InteractionStatus.None,
    signIn,
    signOut,
    getAccessToken,
  };
}
